import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CUSTOMER_STATUS_META } from '@goelectric/ui-components';
import type {
  AllySummary,
  Customer,
  CustomerStatus,
  CustomerType,
  PageMeta,
} from '@goelectric/shared-types';
import { allyDisplayName } from '@/features/aliados/utils/format';
import type { CustomerFilters } from '../hooks/useCustomers';
import { CustomerStatusBadge, CustomerTypeBadge } from './CustomerBadges';
import { PendingMetric } from './PendingMetric';

/** Debounce de la búsqueda antes de golpear el backend (server-side). */
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_OPTIONS: { value: CustomerStatus; label: string }[] = (['ACTIVE', 'SUSPENDED'] as const).map(
  (value) => ({ value, label: CUSTOMER_STATUS_META[value].label }),
);

const CUSTOMER_TYPE_OPTIONS: { value: CustomerType; label: string }[] = [
  { value: 'CONDUCTOR', label: 'Conductores' },
  { value: 'FLOTA', label: 'Flotas' },
];

export interface CustomersTableProps {
  customers: Customer[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: CustomerFilters;
  /** Muestra el CTA del empty-state; solo con permiso de escritura. */
  canCreate: boolean;
  /**
   * Muestra el filtro por aliado. Solo `SUPER_ADMIN`: el contrato hace efectivo el
   * `aliadoId` solo para ese rol. `ALIADO_ADMIN` (y los demás no lo ven) opera sin él.
   */
  canFilterByAlly: boolean;
  /** Aliados para el selector del filtro (`?aliadoId`), solo visible a `SUPER_ADMIN`. */
  allies: AllySummary[];
  onFiltersChange: (filters: CustomerFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (customerId: string) => void;
  /** Navega a la flota cuando la fila es un cliente `FLOTA` con `fleetId`. */
  onSelectFleet?: (fleetId: string) => void;
}

/** `true` si hay algún filtro aplicado (para el empty-state). */
function hasActiveFilters(filters: CustomerFilters): boolean {
  return Boolean(filters.q || filters.status || filters.tipoCliente || filters.aliadoId);
}

/**
 * Tabla CRUD de Clientes (guía de estilo §8): búsqueda, filtro por estado, filtro por
 * aliado (solo `SUPER_ADMIN`), paginación server-side con el `meta` del contrato,
 * empty-state con acción sugerida y badge de estado. El `Ver` navega al detalle.
 */
export function CustomersTable({
  customers,
  meta,
  loading,
  error,
  filters,
  canCreate,
  canFilterByAlly,
  allies,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
  onSelectFleet,
}: CustomersTableProps) {
  const [query, setQuery] = useState(filters.q ?? '');

  // Sincroniza la búsqueda con el backend con debounce (evita una request por tecla).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = query.trim();
      if ((filters.q ?? '') !== next) {
        onFiltersChange({ ...filters, q: next || undefined });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, filters, onFiltersChange]);

  const allyOptions = useMemo(
    () => allies.map((ally) => ({ value: ally.id, label: allyDisplayName(ally) })),
    [allies],
  );

  const filtered = hasActiveFilters(filters);
  // La columna "Flota" solo aparece si hay información de flota en la página: en clientes
  // de plataforma sin flota no se muestra una columna vacía.
  const showFleetColumn = customers.some(
    (customer) => customer.tipoCliente === 'FLOTA' || customer.fleetId || customer.fleetName,
  );

  /** La Cuenta Maestra navega a su flota; el conductor sigue al detalle de cliente. */
  const selectCustomer = useCallback(
    (customer: Customer) => {
      if (customer.tipoCliente === 'FLOTA' && customer.fleetId && onSelectFleet) {
        onSelectFleet(customer.fleetId);
        return;
      }
      onSelect(customer.id);
    },
    [onSelect, onSelectFleet],
  );

  const columns = useMemo<ColumnsType<Customer>>(
    () => [
      {
        title: 'Cliente',
        key: 'customer',
        fixed: 'left',
        width: 260,
        render: (_, customer) => (
          <div className="crm-cell-stack">
            <Typography.Text strong title={customer.fullName}>
              {customer.fullName}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              title={customer.id}
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {customer.id}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Tipo',
        key: 'tipoCliente',
        width: 130,
        render: (_, customer) => <CustomerTypeBadge tipoCliente={customer.tipoCliente} />,
      },
      {
        title: 'Contacto',
        key: 'contact',
        width: 240,
        render: (_, customer) => (
          <div className="crm-cell-stack">
            <span title={customer.phone}>{customer.phone}</span>
            <Typography.Text type="secondary" title={customer.email || 'Sin email'} style={{ fontSize: 12 }}>
              {customer.email || 'Sin email'}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Aliados con actividad',
        key: 'aliados',
        width: 260,
        render: (_, customer) => {
          const aliados = customer.aliados ?? [];
          if (aliados.length === 0) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }
          return (
            <Space size={[0, 4]} wrap>
              {aliados.map((aliado) => (
                <Tag key={aliado.aliadoId} color="blue" title={aliado.nombre}>
                  {aliado.nombre}
                </Tag>
              ))}
            </Space>
          );
        },
      },
      ...(showFleetColumn
        ? [
            {
              title: 'Flota',
              key: 'fleet',
              width: 220,
              ellipsis: true,
              render: (_: unknown, customer: Customer) =>
                customer.fleetName ? (
                  <Typography.Text title={customer.fleetName}>{customer.fleetName}</Typography.Text>
                ) : customer.fleetId ? (
                  <Typography.Text
                    type="secondary"
                    title={customer.fleetId}
                    style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
                  >
                    {customer.fleetId}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
            } satisfies ColumnsType<Customer>[number],
          ]
        : []),
      {
        title: 'Sesiones',
        dataIndex: 'totalSessions',
        key: 'totalSessions',
        width: 110,
        align: 'right',
        // La métrica ausente (US 2.9) no es un 0: se ordena al final, nunca como 0.
        sorter: (a, b) =>
          (a.totalSessions ?? Number.POSITIVE_INFINITY) -
          (b.totalSessions ?? Number.POSITIVE_INFINITY),
        render: (value: number | undefined) =>
          value !== undefined ? value : <PendingMetric />,
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status: CustomerStatus) => <CustomerStatusBadge status={status} />,
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 90,
        align: 'right',
        render: (_, customer) => (
          <Button size="small" onClick={() => selectCustomer(customer)}>
            {customer.tipoCliente === 'FLOTA' && customer.fleetId ? 'Ver flota' : 'Ver'}
          </Button>
        ),
      },
    ],
    [selectCustomer, showFleetColumn],
  );

  const clearFilters = () => {
    setQuery('');
    onFiltersChange({});
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre o teléfono"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 300 }}
          aria-label="Buscar clientes"
        />
        <Select<CustomerStatus | 'ALL'>
          value={filters.status ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({ ...filters, status: value === 'ALL' ? undefined : value })
          }
          style={{ width: 180 }}
          aria-label="Filtrar por estado"
          options={[{ value: 'ALL', label: 'Todos los estados' }, ...STATUS_OPTIONS]}
        />
        <Select<CustomerType | 'ALL'>
          value={filters.tipoCliente ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({ ...filters, tipoCliente: value === 'ALL' ? undefined : value })
          }
          style={{ width: 190 }}
          aria-label="Filtrar por tipo de cliente"
          options={[{ value: 'ALL', label: 'Todos los tipos' }, ...CUSTOMER_TYPE_OPTIONS]}
        />
        {canFilterByAlly && (
          <Select<string>
            showSearch
            allowClear
            optionFilterProp="label"
            value={filters.aliadoId}
            onChange={(value) => onFiltersChange({ ...filters, aliadoId: value })}
            placeholder="Todos los aliados"
            style={{ width: 240 }}
            aria-label="Filtrar por aliado"
            options={allyOptions}
          />
        )}
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los clientes"
          description={error}
          action={
            <Button size="small" onClick={onRetry}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Table<Customer>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={customers}
          scroll={{ x: 1220 }}
          rowClassName="clientes-row"
          onRow={(customer) => ({ onClick: () => selectCustomer(customer) })}
          pagination={{
            current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} clientes`,
            onChange: (page) => onOffsetChange((page - 1) * meta.limit),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={filtered ? 'Sin clientes para los filtros' : 'Aún no hay clientes'}
              >
                {filtered ? (
                  <Button onClick={clearFilters}>Limpiar filtros</Button>
                ) : (
                  canCreate && (
                    <Button type="primary" onClick={onCreate}>
                      Crear el primer cliente
                    </Button>
                  )
                )}
              </Empty>
            ),
          }}
        />
      )}
    </div>
  );
}
